'use client';

import Blurb from '@commons/Blurb/Blurb';
import BlurbList from '@commons/BlurbList/BlurbList';
import Heading from '@commons/Heading/Heading';
import Button from '@commons/Button';
import './_splx-list-block.scss';
import Paginator from '@commons/Paginator/Paginator';
import { useDesitnationCollection } from '@commons/DesitnationCollection/useDesitnationCollection';
import { useDestinationFilter } from '@commons/DestinationFilter/useDestinationFilter';
import useDictionary from '@modules/Dictionary/useDictionary';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import HtmlToJsx from '@commons/HtmlToJsx/HtmlToJsx';
import { useSupertag } from '@commons/SupertagProvider/SupertagProvider';
import MultiLocationMap from '@components/LocationMap/MultiLocationMap';
import filterAndSort from '@utils/filterAndSort';

const defaultPosts = [];
const defaultTaxonomyTerms = [];

export default function SplxListBlock({
  headingLevel = 3,
  heading = '',
  description = '',
  posts = defaultPosts,
  layout = 'carousel-square',
  bg_color = 'transparent',
  taxonomyTerms = defaultTaxonomyTerms,
  include_location_map = 'no',
  anchor = '',
  gradients = {},
  posts_per_page,
  preFiltered = false
}) {
  const blurbHeadingLevel = heading ? headingLevel + 1 : 3;
  const { dictionary } = useDictionary();
  const { supertag } = useSupertag();
  const { categories, allCategories, tags, products, events, pages } = useDesitnationCollection();
  const { categoryFilters } = useDestinationFilter();
  const firstPostType = posts[0]?.post_type;
  const [paginatorNeeded, setPaginatorNeeded] = useState(false);

  posts_per_page = parseInt(posts_per_page, 10) || (posts.length > 0 ? posts.length : 20);

  const [visibleCount, setVisibleCount] = useState(posts_per_page);

  const cats = /** @type {any[] | undefined} */ (allCategories ?? categories);
  const tgs = /** @type {any[] | undefined} */ (tags);
  const st = /** @type {any[] | undefined} */ (supertag);
  const cf = /** @type {{id: number}[] | undefined} */ (categoryFilters);

  const enrichWithTags = useCallback(
    (/** @type {any} */ p) => {
      if (!cats?.length) return p;
      const postCategories = cats.filter((c) => p.categories?.includes(c.id));
      const postTags = tgs?.filter((t) => p.tags?.includes(t.id)) || [];
      return { ...p, tags: [...postCategories, ...postTags] };
    },
    [cats, tgs]
  );

  const enrichedPosts = useMemo(() => posts.map(enrichWithTags), [posts, enrichWithTags]);

  const { filteredPosts, debugInfo } = useMemo(() => {
    const hasFilters = (st?.length ?? 0) > 0 || (cf?.length ?? 0) > 0;
    const isGridList = layout.startsWith('grid-') && (firstPostType === 'product' || firstPostType === 'event');

    if (preFiltered) {
      const sorted = [...enrichedPosts].sort((a, b) => {
        const da = a.display_date || a.date || a.start_date || '';
        const db = b.display_date || b.date || b.start_date || '';
        return da < db ? -1 : da > db ? 1 : 0;
      });
      return { filteredPosts: sorted, debugInfo: '{}' };
    }

    if (!hasFilters && !isGridList) {
      return {
        filteredPosts: enrichedPosts,
        debugInfo: JSON.stringify(
          {
            mode: 'no-filter',
            firstPostType,
            basePosts: posts.length,
            shown: enrichedPosts.length
          },
          null,
          2
        )
      };
    }

    const slugToField = (/** @type {string} */ slug) =>
      slug === 'category' ? 'categories' : slug === 'super_tag' ? 'supertags' : slug === 'post_tag' ? 'tags' : null;

    /** @type {{ field: string, ids: (number|string)[] }[]} */
    const filters = [];
    for (const t of taxonomyTerms || []) {
      if (!t.terms?.length) continue;
      const field = slugToField(t.slug);
      if (!field) continue;
      filters.push({ field, ids: t.terms });
    }
    if (st?.length) {
      filters.push({ field: 'supertags', ids: st });
    }
    if (cf?.length) {
      filters.push({ field: 'categories', ids: cf.map((f) => f.id) });
    }

    /** @type {any[]} */
    const pool =
      firstPostType === 'product'
        ? products || []
        : firstPostType === 'event'
          ? events || []
          : firstPostType === 'page'
            ? pages || []
            : [];

    const noFilters = filters.length === 0;

    const _posts = filterAndSort(enrichedPosts, filters, { includeNoMatch: noFilters });
    const takenIds = new Set(_posts.map((/** @type {any} */ p) => p.id));
    const poolRest = pool.filter((/** @type {any} */ p) => !takenIds.has(p.id));
    const poolMatched = filterAndSort(poolRest, filters, { includeNoMatch: false }).length;

    const includeNoMatchExtra = isGridList ? noFilters : true;
    const _extra = filterAndSort(poolRest, filters, { includeNoMatch: includeNoMatchExtra }).map(enrichWithTags);

    const result =
      isGridList && !preFiltered
        ? [..._posts, ..._extra]
        : isGridList
          ? _posts
          : [..._posts, ..._extra].slice(0, posts_per_page);

    return {
      filteredPosts: result,
      debugInfo: JSON.stringify(
        {
          mode: isGridList ? 'grid-list-expanded' : 'filter',
          firstPostType,
          filters,
          basePosts: posts.length,
          poolSize: pool.length,
          postsMatched: _posts.length,
          poolRest: poolRest.length,
          poolMatched,
          extra: _extra.length,
          shown: result.length
        },
        null,
        2
      )
    };
  }, [
    enrichedPosts,
    st,
    cf,
    taxonomyTerms,
    products,
    events,
    pages,
    firstPostType,
    posts.length,
    enrichWithTags,
    layout,
    posts_per_page,
    preFiltered
  ]);

  const displayPosts = useMemo(() => {
    if (!preFiltered) return filteredPosts;
    return [...filteredPosts].sort((a, b) => {
      const da = a.display_date || a.date || a.start_date || '';
      const db = b.display_date || b.date || b.start_date || '';
      return da < db ? -1 : da > db ? 1 : 0;
    });
  }, [filteredPosts, preFiltered]);

  useEffect(() => {
    setVisibleCount(posts_per_page);
  }, [displayPosts, posts_per_page]);

  const numDots = Math.min(4, displayPosts.length);
  const carouselDots = [];
  for (let i = 0; i < numDots; i++) {
    carouselDots.push({
      index: i
    });
  }

  const blurbRefs = useRef([]);

  const setRef = useCallback(
    (index) => (element) => {
      blurbRefs.current[index] = element;
    },
    []
  );

  function handleIntersection(entries, observer) {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        entry?.target?.classList.remove('Blurb--out-of-view');
      } else {
        entry?.target?.classList.add('Blurb--out-of-view');
      }
    });
    setPaginatorNeeded(entries.filter((entry) => entry.isIntersecting).length !== displayPosts.length);
  }

  useEffect(() => {
    blurbRefs.current = blurbRefs.current.slice(0, displayPosts.length);

    if (displayPosts.length) {
      // Options for the observer (as root is omitted, entire screen is default viewport)
      const options = {
        rootMargin: '100% 0% 100% 0%', // have the observer only react to horizontal intersections
        threshold: 1.0 // trigger intersection for entire/whole entry only
      };
      const observer = new IntersectionObserver(handleIntersection, options);
      blurbRefs.current?.forEach((blurb) => {
        if (blurb) {
          observer.observe(blurb);
        }
      });

      return () => observer.disconnect();
    }
  }, [filteredPosts]);

  return displayPosts.length ? (
    <div className="SplxListBlock Layout--subgrid" id={anchor} data-info={JSON.stringify(debugInfo)}>
      {heading && (
        <Heading className="SplxListBlock__heading" level={headingLevel}>
          {heading}
        </Heading>
      )}
      {description && (
        <div className="SplxListBlock__description">
          <HtmlToJsx html={description} />
        </div>
      )}
      {layout.startsWith('carousel-') ? (
        <>
          <Paginator.Provider items={displayPosts} defaultIndex={displayPosts.length > 3 ? 1 : 0}>
            <div className={`SplxListBlock--${layout} SplxListBlock__carouselWrapper Layout--subgrid`}>
              <BlurbList.Carousel className={`SplxListBlock--${bg_color}`}>
                {displayPosts?.map((post, index) => (
                  <Blurb
                    variant={layout.split('carousel-')[1]}
                    key={index}
                    ref={setRef(index)}
                    style={
                      layout.includes('wide') && gradients[post.id || index]
                        ? { '--overlay': gradients[post.id || index] }
                        : undefined
                    }
                  >
                    <Blurb.Image src={post.image} alt={post.post_title} />
                    {layout.split('carousel-')[1] !== 'wide' && post.tags?.length ? (
                      <div className="Blurb__tags">
                        {post.tags.map((tag, index) => (
                          <Blurb.Tag key={index} href={tag.path}>
                            <HtmlToJsx html={tag.title} />
                          </Blurb.Tag>
                        ))}
                      </div>
                    ) : null}
                    <Blurb.Link href={post.url} overlay></Blurb.Link>
                    <Blurb.Content>
                      {layout.endsWith('-cta') ? (
                        post.post_type === 'product' && post.rating ? (
                          <Blurb.Rating rating={post.rating} />
                        ) : post.post_type === 'event' && post.start_date ? (
                          <Blurb.Date
                            date={post.display_date || post.start_date}
                            endDate={post.display_date ? post.display_end_date : post.end_date}
                          />
                        ) : null
                      ) : null}
                      <Blurb.Title level={blurbHeadingLevel}>
                        <HtmlToJsx html={post.title} />
                      </Blurb.Title>
                      <Blurb.Description>
                        <HtmlToJsx html={post.excerpt} />
                      </Blurb.Description>
                      {layout.endsWith('-cta') ? (
                        <>
                          {post.address ? <Blurb.Address>{post.address}</Blurb.Address> : null}
                          <Blurb.CTA
                            primaryText={post.booking_url ? dictionary?.product?.bookNow || 'Bestill nå!' : ''}
                            primaryHref={post.booking_url}
                            secondaryText={post.url ? dictionary?.blurb?.readMore || 'Les mer' : ''}
                            secondaryHref={post.url || ''}
                          />
                        </>
                      ) : null}
                    </Blurb.Content>
                  </Blurb>
                ))}
              </BlurbList.Carousel>
            </div>
            {paginatorNeeded ? (
              <Paginator variant="dots">
                <Paginator.ItemBack />
                {carouselDots?.map((dot, index) => (
                  <Paginator.ItemDots key={index} activeIndex={index} item={dot} />
                ))}
                <Paginator.ItemForward />
              </Paginator>
            ) : null}
          </Paginator.Provider>
          {include_location_map === 'yes' ? (
            <MultiLocationMap
              locations={displayPosts
                .filter((post) => post.long && post.lat)
                .map((post) => ({ lat: parseFloat(post.lat), long: parseFloat(post.long), name: post.title }))}
            />
          ) : null}
        </>
      ) : null}
      {layout === 'vertical' ? (
        <>
          <BlurbList.Vertical>
            {displayPosts?.map((post) => (
              <Blurb variant="vertical" key={post.id}>
                <Blurb.Image src={post.image} alt={post.title} />
                <Blurb.Link href={post.url} overlay></Blurb.Link>
                <Blurb.Content>
                  <Blurb.Title level={Math.max(blurbHeadingLevel - 1, 3)}>
                    <HtmlToJsx html={post.title} />
                  </Blurb.Title>
                  <Blurb.Description>
                    <HtmlToJsx html={post.excerpt} />
                  </Blurb.Description>
                  {post.tags?.length ? (
                    <div className="Blurb__tags">
                      {post.tags.map((tag, index) => (
                        <Blurb.Tag key={index} href={tag.path}>
                          <HtmlToJsx html={tag.title} />
                        </Blurb.Tag>
                      ))}
                    </div>
                  ) : null}
                </Blurb.Content>
              </Blurb>
            ))}
          </BlurbList.Vertical>
          {include_location_map === 'yes' ? (
            <MultiLocationMap
              locations={displayPosts
                .filter((post) => post.long && post.lat)
                .map((post) => ({ lat: parseFloat(post.lat), long: parseFloat(post.long) }))}
            />
          ) : null}
        </>
      ) : null}
      {layout === 'grid-cta' ? (
        <>
          <BlurbList.Columns columns={3} className={`SplxListBlock--${bg_color}`}>
            {displayPosts?.slice(0, visibleCount).map((post) => (
              <Blurb key={post.id} variant={layout}>
                <Blurb.Image src={post.image} alt={post.post_title} />
                <Blurb.Link href={post.url} overlay></Blurb.Link>
                {post.tags?.length ? (
                  <div className="Blurb__tags">
                    {post.tags.map((tag, index) => (
                      <Blurb.Tag key={index} href={tag.path}>
                        <HtmlToJsx html={tag.title} />
                      </Blurb.Tag>
                    ))}
                  </div>
                ) : null}
                <Blurb.Content>
                  {post.post_type === 'product' && post.rating ? (
                    <Blurb.Rating rating={post.rating} />
                  ) : post.post_type === 'event' && post.start_date ? (
                    <Blurb.Date
                      date={post.display_date || post.start_date}
                      endDate={post.display_date ? post.display_end_date : post.end_date}
                    />
                  ) : null}
                  <Blurb.Title level={blurbHeadingLevel}>{post.title}</Blurb.Title>
                  <Blurb.Description>
                    <HtmlToJsx html={post.excerpt} />
                  </Blurb.Description>
                  {post.address ? <Blurb.Address>{post.address}</Blurb.Address> : null}
                  <Blurb.CTA
                    primaryText={post.booking_url ? dictionary?.product?.bookNow || 'Bestill nå!' : ''}
                    primaryHref={post.booking_url}
                    secondaryText={post.url ? dictionary?.blurb?.readMore || 'Les mer' : ''}
                    secondaryHref={post.url || ''}
                  />
                </Blurb.Content>
              </Blurb>
            ))}
          </BlurbList.Columns>
          {displayPosts.length > visibleCount ? (
            <div className="SplxListBlock__load-more">
              <Button
                className="SplxListBlock__load-more-button"
                priority="secondary"
                onClick={() => {
                  setVisibleCount((c) => c + 20);
                }}
              >
                {dictionary?.archive?.loadMore || 'Load more'}
              </Button>
            </div>
          ) : null}
          {include_location_map === 'yes' ? (
            <MultiLocationMap
              locations={displayPosts
                .filter((post) => post.long && post.lat)
                .map((post) => ({ lat: parseFloat(post.lat), long: parseFloat(post.long) }))}
            />
          ) : null}
        </>
      ) : null}
    </div>
  ) : null;
}
